// FamilyFavoritesCard — "what this household actually loves," on Home.
//
// Ranks the family's last-90-day cooks with the community
// leaderboard's Bayesian score (useFamilyCookStats) and shows the
// top three: title, how many times it was cooked, who cooks it, and
// how it lands. Tapping a row jumps straight into cooking it when
// the dish resolves to a cookable recipe (bundled or library).
//
// Renders nothing when there's no family (viewer-only counts already
// live on the recipe pages) or when no dish has been cooked at least
// twice in the window — a one-off isn't a favorite yet.

import { useMemo } from "react";
import { FONT, COLOR } from "../lib/tokens";
import { useTheme as useMCMTheme } from "../lib/theme";
import { useFamilyCookStats } from "../lib/useFamilyCookStats";

function ratingWord(avg) {
  if (avg == null) return null;
  if (avg >= 2.5) return "USUALLY NAILED";
  if (avg >= 1.75) return "CROWD-PLEASER";
  if (avg >= 1) return "MIXED REVIEWS";
  return null;
}

export default function FamilyFavoritesCard({
  userId,
  familyIds = [],
  nameFor = null,        // (id) => display name
  resolveRecipe = null,  // (slug) => recipe object | null
  onCookRecipe = null,   // (recipe) => void
}) {
  const { theme } = useMCMTheme();
  const familyKey = useMemo(() => [...familyIds].sort().join(","), [familyIds]);
  const { favorites } = useFamilyCookStats(
    familyIds.length > 0 ? userId : null,   // no family → no query at all
    familyKey,
  );

  const top = useMemo(
    () => favorites.filter((f) => f.cookCount >= 2).slice(0, 3),
    [favorites],
  );

  if (familyIds.length === 0 || top.length === 0) return null;

  const chefLine = (f) => {
    const names = f.chefIds
      .map((id) => (nameFor ? nameFor(id) : null))
      .filter(Boolean)
      .map((n) => String(n).split(/\s+/)[0]);
    if (names.length === 0) return null;
    if (names.length === 1) return `by ${names[0]}`;
    if (names.length === 2) return `by ${names[0]} & ${names[1]}`;
    return `by ${names[0]} +${names.length - 1}`;
  };

  return (
    <div style={{
      margin: "12px 20px 0", padding: "14px 16px",
      background: theme.color.glassFillHeavy || theme.color.glassFillLite,
      backdropFilter: "blur(14px)",
      WebkitBackdropFilter: "blur(14px)",
      border: `1px solid ${theme.color.glassBorder}`,
      borderRadius: 24,
      boxShadow: theme.shadow?.glass,
    }}>
      <div style={{
        fontFamily: FONT.mono, fontSize: 12, fontWeight: 600,
        color: theme.color.ink, opacity: 0.75,
        letterSpacing: "0.16em", marginBottom: 10,
      }}>
        FAMILY FAVORITES · 90 DAYS
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {top.map((f, i) => {
          const recipe = resolveRecipe ? resolveRecipe(f.slug) : null;
          const tappable = !!recipe && !!onCookRecipe;
          const chefs = chefLine(f);
          const word = ratingWord(f.avgRating);
          return (
            <button
              key={f.slug}
              type="button"
              className={tappable ? "mcm-focusable" : undefined}
              onClick={tappable ? () => onCookRecipe(recipe) : undefined}
              disabled={!tappable}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "8px 10px",
                background: "transparent",
                border: "none", borderRadius: 12,
                textAlign: "left", width: "100%",
                cursor: tappable ? "pointer" : "default",
                touchAction: "manipulation",
              }}
            >
              <span style={{
                fontFamily: FONT.mono, fontSize: 13, fontWeight: 700,
                color: theme.color.ink, opacity: 0.5,
                width: 16, flexShrink: 0,
              }}>
                {i + 1}
              </span>
              {f.emoji && <span style={{ fontSize: 20, flexShrink: 0 }}>{f.emoji}</span>}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontFamily: FONT.itemTitle, fontStyle: "italic", fontWeight: 500,
                  fontSize: 15, color: theme.color.ink,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {f.title}
                </div>
                <div style={{
                  fontFamily: FONT.mono, fontSize: 12, color: theme.color.ink,
                  opacity: 0.65, letterSpacing: "0.04em", marginTop: 1,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {f.cookCount}×{chefs ? ` · ${chefs}` : ""}{word ? ` · ${word}` : ""}
                </div>
              </div>
              {tappable && (
                <span style={{
                  fontFamily: FONT.mono, fontSize: 12, fontWeight: 600,
                  color: COLOR.leaf, letterSpacing: "0.08em", flexShrink: 0,
                }}>
                  COOK →
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
